import React, { useMemo } from 'react';
import { useData } from '../context/DataContext';

/* ============================================================
   Aides dates (les anciennes données peuvent être des numéros
   de série Excel, les nouvelles des chaînes ISO)
   ============================================================ */
const MS_PER_DAY = 86400000;
const EXCEL_OFFSET_MS = 25569 * MS_PER_DAY;

const dateToMS = (v) => {
  if (typeof v === 'number') return v * MS_PER_DAY - EXCEL_OFFSET_MS;
  const s = String(v || '');
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return Date.UTC(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(s);
  return isNaN(d.getTime()) ? -Infinity : d.getTime();
};

const monthIndexOf = (v) => {
  const s = String(v || '');
  const m = s.match(/^\d{4}-(\d{2})/);
  if (m) return parseInt(m[1], 10) - 1;
  const d = new Date(dateToMS(v));
  return isNaN(d.getTime()) ? -1 : d.getMonth();
};

const MOIS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

const euro = (n) =>
  (Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const tvaNetClass = (net) => (net >= 0 ? 'positive' : 'negative');

const structureTVAOf = (cs) =>
  (cs || []).reduce((acc, c) => acc + (parseFloat(c.montantHT) || 0) * (parseFloat(c.tva) || 0), 0);

const Tva = () => {
  const { factures, couts, coutsStructure } = useData();

  /* --- Agrégats TVA (déductible = achats directs + charges de structure) --- */
  const total = useMemo(() => {
    const collectee = (factures || []).reduce((acc, f) => acc + (parseFloat(f.tva) || 0), 0);
    const deductibleDirects = (couts || []).reduce(
      (acc, c) => acc + (parseFloat(c.montantHT) || 0) * (parseFloat(c.tva) || 0),
      0
    );
    const deductibleStructure = structureTVAOf(coutsStructure);
    const deductible = deductibleDirects + deductibleStructure;
    return { collectee, deductible, deductibleStructure, nette: collectee - deductible };
  }, [factures, couts, coutsStructure]);

  /* --- TVA par mois (année en cours, mois présentés du 1er au 12e) --- */
  const parMois = useMemo(() => {
    return MOIS.map((mois, idx) => {
      const collectee = (factures || [])
        .filter(f => monthIndexOf(f.date) === idx)
        .reduce((acc, f) => acc + (parseFloat(f.tva) || 0), 0);
      const deductible = (couts || [])
        .filter(c => monthIndexOf(c.date) === idx)
        .reduce((acc, c) => acc + (parseFloat(c.montantHT) || 0) * (parseFloat(c.tva) || 0), 0)
        + structureTVAOf((coutsStructure || []).filter(c => monthIndexOf(c.date) === idx));
      return { mois, collectee, deductible, nette: collectee - deductible };
    }).filter(m => m.collectee !== 0 || m.deductible !== 0);
  }, [factures, couts, coutsStructure]);

  /* --- Détail par chantier (client associé, CA, coûts, TVA) --- */
  const parChantier = useMemo(() => {
    const map = {};
    const add = (cle, client, collectee, deductible) => {
      if (!cle) return;
      if (!map[cle]) map[cle] = { chantier: cle, client: client || '', ca: 0, coutsHT: 0, collectee: 0, deductible: 0 };
      if (client) map[cle].client = client;
      map[cle].collectee += collectee;
      map[cle].deductible += deductible;
    };
    (factures || []).forEach(f => {
      add(f.chantier, f.client, parseFloat(f.tva) || 0, 0);
      if (f.chantier && map[f.chantier]) map[f.chantier].ca += parseFloat(f.montantHT) || 0;
    });
    (couts || []).forEach(c => {
      const deduct = (parseFloat(c.montantHT) || 0) * (parseFloat(c.tva) || 0);
      add(c.chantier, c.client, 0, deduct);
      if (c.chantier && map[c.chantier]) map[c.chantier].coutsHT += parseFloat(c.montantHT) || 0;
    });
    return Object.values(map)
      .map(m => ({ ...m, nette: m.collectee - m.deductible }))
      .sort((a, b) => b.ca - a.ca);
  }, [factures, couts]);

  /* --- Détail par client --- */
  const parClient = useMemo(() => {
    const map = {};
    const add = (client, collectee, deductible) => {
      if (!client) return;
      if (!map[client]) map[client] = { client, ca: 0, collectee: 0, deductible: 0 };
      map[client].collectee += collectee;
      map[client].deductible += deductible;
    };
    (factures || []).forEach(f => {
      add(f.client, parseFloat(f.tva) || 0, 0);
      if (f.client && map[f.client]) map[f.client].ca += parseFloat(f.montantHT) || 0;
    });
    (couts || []).forEach(c => add(c.client, 0, (parseFloat(c.montantHT) || 0) * (parseFloat(c.tva) || 0)));
    return Object.values(map)
      .map(m => ({ ...m, nette: m.collectee - m.deductible }))
      .sort((a, b) => b.ca - a.ca);
  }, [factures, couts]);

  const hasData =
    (factures || []).length > 0 || (couts || []).length > 0 || (coutsStructure || []).length > 0;
  const nette = total.nette;

  return (
    <div>
      <h2>🧾 TVA — collectée vs déductible</h2>
      <p className="info-text">
        <strong>TVA collectée</strong> = TVA facturée à vos clients sur les ventes ·
        <strong> TVA déductible</strong> = TVA sur vos achats de marchandises, la sous-traitance
        <strong> et vos charges de structure</strong>.
        La <strong>TVA nette</strong> est le montant à reverser à l'État (ou un crédit si négatif).
      </p>

      <div className="cout-summary">
        <div className="cout-summary-item">
          <span>TVA collectée</span>
          <strong>{euro(total.collectee)}</strong>
        </div>
        <div className="cout-summary-item">
          <span>TVA déductible</span>
          <strong>{euro(total.deductible)}</strong>
        </div>
        <div className="cout-summary-item">
          <span>TVA nette</span>
          <strong style={{ color: nette >= 0 ? 'var(--color-success)' : 'var(--color-error)' }}>
            {euro(nette)}
          </strong>
        </div>
      </div>
      <p className="preview-note">
        dont TVA déductible sur charges de structure : <strong>{euro(total.deductibleStructure)}</strong>.
      </p>

      {!hasData && (
        <section className="top-section">
          <p className="info-text">Aucune donnée — importez vos factures/devis ou saisissez des coûts.</p>
        </section>
      )}

      <section className="top-section" aria-label="TVA par mois">
        <h3>📅 TVA par mois</h3>
        <table className="data-table" role="table">
          <thead>
            <tr>
              <th>Mois</th>
              <th>TVA collectée</th>
              <th>TVA déductible</th>
              <th>TVA nette</th>
            </tr>
          </thead>
          <tbody>
            {parMois.map((m, i) => (
              <tr key={i}>
                <td>{m.mois}</td>
                <td>{euro(m.collectee)}</td>
                <td>{euro(m.deductible)}</td>
                <td className={tvaNetClass(m.nette)}>{euro(m.nette)}</td>
              </tr>
            ))}
            {parMois.length === 0 && hasData && (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', color: 'var(--color-text-secondary)', padding: '2rem' }}>
                  Aucune TVA ce mois-ci.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <section className="top-section" aria-label="TVA par chantier">
        <h3>🏗️ TVA par chantier</h3>
        <table className="data-table" role="table">
          <thead>
            <tr>
              <th>Chantier</th>
              <th>Client</th>
              <th>CA facturé HT</th>
              <th>TVA collectée</th>
              <th>TVA déductible</th>
              <th>TVA nette</th>
            </tr>
          </thead>
          <tbody>
            {parChantier.map((c, i) => (
              <tr key={i}>
                <td>{c.chantier}</td>
                <td>{c.client || '—'}</td>
                <td>{euro(c.ca)}</td>
                <td>{euro(c.collectee)}</td>
                <td>{euro(c.deductible)}</td>
                <td className={tvaNetClass(c.nette)}>{euro(c.nette)}</td>
              </tr>
            ))}
            {parChantier.length === 0 && hasData && (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', color: 'var(--color-text-secondary)', padding: '2rem' }}>
                  Aucune TVA par chantier.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <section className="top-section" aria-label="TVA par client">
        <h3>👤 TVA par client</h3>
        <table className="data-table" role="table">
          <thead>
            <tr>
              <th>Client</th>
              <th>CA facturé HT</th>
              <th>TVA collectée</th>
              <th>TVA déductible</th>
              <th>TVA nette</th>
            </tr>
          </thead>
          <tbody>
            {parClient.map((c, i) => (
              <tr key={i}>
                <td>{c.client}</td>
                <td>{euro(c.ca)}</td>
                <td>{euro(c.collectee)}</td>
                <td>{euro(c.deductible)}</td>
                <td className={tvaNetClass(c.nette)}>{euro(c.nette)}</td>
              </tr>
            ))}
            {parClient.length === 0 && hasData && (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', color: 'var(--color-text-secondary)', padding: '2rem' }}>
                  Aucune TVA par client.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default Tva;
